import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { apiCallable } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AddDeviceDialog } from '@/components/AddDeviceDialog'
import { CreateGroupDialog } from '@/components/CreateGroupDialog'
import { GroupSettingsDialog } from '@/components/GroupSettingsDialog'
import { PersonsTab } from '@/components/PersonsTab'
import { PersonDetailDialog } from '@/components/PersonDetailDialog'
import { SightingsTab } from '@/components/SightingsTab'
import { MergeSuggestionsTab } from '@/components/MergeSuggestionsTab'
import { ClassificationsList } from '@/components/ClassificationsList'
import { FeedView } from '@/components/feed/FeedView'
import { useTelegramLink } from '@/hooks/useTelegramLink'
import { TelegramLinkDialog } from '@/components/TelegramLinkDialog'
import { Plus, LogOut, Settings, Users, Shield, MessageCircle } from 'lucide-react'
import { TimezoneSelector } from '@/components/TimezoneSelector'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { formatDateTime } from '@/lib/dateTime'
import type { Device } from '@/types'

const SELECTED_GROUP_KEY = 'edge_manager_selected_group'

interface MemberPermissions {
  canManagePersons: boolean
  canManageClassifications: boolean
  feedOnly: boolean
}

interface MemberAlertPrefs {
  alertsEnabled: boolean
  alertClassificationIds?: string[]
  classificationIds: string[]
}

interface UserGroup {
  id: string
  name: string
  memberCount: number
  isAdmin: boolean
}

export function DeviceList() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, logout } = useAuth()
  const { linked: telegramLinked, refetch: refetchTelegram } = useTelegramLink()
  const [devices, setDevices] = useState<Device[]>([])
  const [userGroups, setUserGroups] = useState<UserGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [devicesLoaded, setDevicesLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false)
  const [groupSettingsDialogOpen, setGroupSettingsDialogOpen] = useState(false)
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false)
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => {
    // Initialize from localStorage if available
    return localStorage.getItem(SELECTED_GROUP_KEY)
  })
  const [groupPermissions, setGroupPermissions] = useState<MemberPermissions | null>(null)
  const [groupAlertPrefs, setGroupAlertPrefs] = useState<MemberAlertPrefs | null>(null)
  const { t } = useTranslation()

  // Derive active tab from URL path
  const rawTab = useMemo(() => {
    if (location.pathname.startsWith('/feed')) return 'feed'
    if (location.pathname.startsWith('/classifications')) return 'classifications'
    if (location.pathname.startsWith('/persons')) return 'persons'
    if (location.pathname.startsWith('/suggestions')) return 'suggestions'
    if (location.pathname.startsWith('/sightings')) return 'sightings'
    if (location.pathname.startsWith('/devices')) return 'devices'
    return 'devices'
  }, [location.pathname])

  // Extract sightingId from URL: /sightings/:sightingId
  // The id is the sighting sk (`${timestamp}#${sightingId}`), percent-encoded when navigating
  // (the '#' would otherwise be parsed as the URL fragment), so decode it back here.
  const urlSightingId = useMemo(() => {
    const match = location.pathname.match(/^\/sightings\/(.+)$/)
    return match ? decodeURIComponent(match[1]) : null
  }, [location.pathname])

  // Extract personId from URL: /persons/:personId
  const urlPersonId = useMemo(() => {
    const match = location.pathname.match(/^\/persons\/(.+)$/)
    return match ? match[1] : null
  }, [location.pathname])

  // The person drawer opened *over* whatever page you are on, via `?personDetail=`.
  // Kept out of the path so the page underneath survives untouched — same tab,
  // same filters, same open sighting dialog — and closing it just drops the param.
  // Not `person`: SightingsTab already owns that one as its person filter.
  const overlayPersonId = searchParams.get('personDetail')

  // Bumped whenever the overlay drawer edits or removes a person, so the list
  // underneath re-reads instead of showing the pre-edit name/classification.
  const [personsRefreshKey, setPersonsRefreshKey] = useState(0)

  // Filter devices by selected group
  const filteredDevices = useMemo(() => {
    if (!selectedGroupId) return devices
    return devices.filter(d => d.groupId === selectedGroupId)
  }, [devices, selectedGroupId])

  // Get selected group name for display
  const selectedGroup = useMemo(() => {
    return userGroups.find(g => g.id === selectedGroupId)
  }, [userGroups, selectedGroupId])

  const isFeedOnly = !!groupPermissions?.feedOnly && !selectedGroup?.isAdmin

  // Non-admins cannot see the devices tab; feed-only members can only see /feed
  const activeTab = useMemo(() => {
    if (isFeedOnly) return 'feed'
    if (rawTab === 'devices' && !selectedGroup?.isAdmin) return 'sightings'
    return rawTab
  }, [rawTab, selectedGroup?.isAdmin, isFeedOnly])

  // Redirect feedOnly users away from any non-feed route
  useEffect(() => {
    if (isFeedOnly && rawTab !== 'feed') {
      navigate('/feed', { replace: true })
    }
  }, [isFeedOnly, rawTab, navigate])

  const loadGroups = async () => {
    try {
      setLoading(true)

      const getUserGroupsWithDetails = apiCallable('getUserGroupsWithDetails')
      const groupsResult = await getUserGroupsWithDetails({})
      const groupsData = groupsResult.data as { success: boolean; groups: UserGroup[] }

      if (groupsData.success) {
        setUserGroups(groupsData.groups)

        if (groupsData.groups.length > 0) {
          const storedGroupId = selectedGroupId || localStorage.getItem(SELECTED_GROUP_KEY)
          const isValidGroup = storedGroupId && groupsData.groups.some(g => g.id === storedGroupId)

          if (isValidGroup) {
            setSelectedGroupId(storedGroupId)
          } else {
            setSelectedGroupId(groupsData.groups[0].id)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load groups:', err)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const loadDevices = async () => {
    try {
      const getUserDevices = apiCallable('getUserDevices')
      const devicesResult = await getUserDevices({})
      const devicesData = devicesResult.data as { success: boolean; devices: Device[] }

      if (devicesData.success) {
        setDevices(devicesData.devices)
        setDevicesLoaded(true)
      }
    } catch (err) {
      console.error('Failed to load devices:', err)
      setError('Failed to load devices')
    }
  }

  useEffect(() => {
    loadGroups()
  }, [])

  // Lazy-load devices only when devices tab is active
  useEffect(() => {
    if (activeTab === 'devices' && !devicesLoaded) {
      loadDevices()
    }
  }, [activeTab, devicesLoaded])

  // Persist selected group to localStorage
  useEffect(() => {
    if (selectedGroupId) {
      localStorage.setItem(SELECTED_GROUP_KEY, selectedGroupId)
    } else {
      localStorage.removeItem(SELECTED_GROUP_KEY)
    }
  }, [selectedGroupId])

  // Fetch group permissions when selected group changes
  useEffect(() => {
    const fetchGroupPermissions = async () => {
      if (!selectedGroupId) {
        setGroupPermissions(null)
        setGroupAlertPrefs(null)
        return
      }

      try {
        const getGroupDetails = apiCallable('getGroupDetails')
        const result = await getGroupDetails({ groupId: selectedGroupId })
        const data = result.data as {
          success: boolean
          group: {
            permissions: MemberPermissions
            alertsEnabled?: boolean
            alertClassificationIds?: string[]
            classificationIds?: string[]
          }
        }

        if (data.success && data.group.permissions) {
          setGroupPermissions(data.group.permissions)
          setGroupAlertPrefs({
            alertsEnabled: data.group.alertsEnabled !== false,
            alertClassificationIds: data.group.alertClassificationIds,
            classificationIds: data.group.classificationIds || [],
          })
        }
      } catch (err) {
        // Fail closed: never widen access on a transient failure. Management
        // capabilities default off so a scoped/feed-only member can't gain
        // elevated rights when getGroupDetails errors.
        console.error('Failed to fetch group permissions:', err)
        setGroupPermissions({
          canManagePersons: false,
          canManageClassifications: false,
          feedOnly: false,
        })
        setGroupAlertPrefs({
          alertsEnabled: true,
          alertClassificationIds: undefined,
          classificationIds: [],
        })
      }
    }

    fetchGroupPermissions()
  }, [selectedGroupId])

  const handleDeviceClick = (deviceId: string) => {
    navigate(`/device/${deviceId}`)
  }

  const handleOpenGroupSettings = (groupId: string) => {
    setCurrentGroupId(groupId)
    setGroupSettingsDialogOpen(true)
  }

  const handleGroupLeft = () => {
    setDevicesLoaded(false)
    loadGroups()
  }

  const handleGroupCreated = () => {
    setDevicesLoaded(false)
    loadGroups()
  }

  // Push, so the browser Back button closes the drawer; the close is a replace,
  // so Back from the closed state leaves the page rather than reopening it.
  const handleNavigateToPerson = (personId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('personDetail', personId)
    setSearchParams(next)
  }

  const handleClosePersonOverlay = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('personDetail')
    setSearchParams(next, { replace: true })
  }

  const handleNavigateToSightings = (personId: string, sightingId?: string, personName?: string) => {
    const params = new URLSearchParams()
    if (personId) params.set('person', personId)
    if (personName) params.set('personName', personName)
    const query = params.toString() ? `?${params.toString()}` : ''

    if (sightingId) {
      // sightingId may be the sighting sk (contains '#'); encode it for the URL path.
      navigate(`/sightings/${encodeURIComponent(sightingId)}${query}`)
    } else {
      navigate(`/sightings${query}`)
    }
  }

  const handleClearPersonFilter = () => {
    navigate('/sightings')
  }

  const handleTabChange = (tab: string) => {
    navigate(`/${tab}`)
  }

  // Render welcome/onboarding screen when user has no groups
  const renderNoGroupsOnboarding = () => (
    <div className="min-h-screen bg-paper">
      <nav className="border-b border-rule bg-paper-2/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <a href="/" className="auvers-logo">
              AUVERS
              <span className="product">Sentry Manager</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[12px] tracking-wider text-ink-soft">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto py-16 px-4">
        <div className="mb-8 text-center">
          <span className="eyebrow">{t('onboarding.welcome')}</span>
        </div>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 grid h-16 w-16 place-items-center rounded-full bg-paper-3 text-gold">
              <Users className="h-7 w-7" strokeWidth={1.4} />
            </div>
            <CardTitle className="text-center">{t('onboarding.welcome')}</CardTitle>
            <hr className="gold-rule mx-auto" />
          </CardHeader>
          <CardContent className="space-y-6">
            {user?.isSuperAdmin ? (
              <>
                <p className="text-center text-[15px] leading-[1.6] text-ink-soft">
                  {t('onboarding.getStarted')}
                </p>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setCreateGroupDialogOpen(true)}
                    className="group relative w-full max-w-xs cursor-pointer rounded-lg border border-rule bg-paper-2 p-6 text-left transition hover:border-gold-soft hover:bg-paper-3"
                  >
                    <div className="mb-3 grid h-12 w-12 place-items-center rounded-full border border-rule bg-paper text-gold">
                      <Plus className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <h3 className="mb-1 font-serif text-[22px] font-medium leading-[1.1] text-ink">
                      {t('onboarding.createTitle')}
                    </h3>
                    <p className="text-[13px] leading-[1.55] text-ink-soft">
                      {t('onboarding.createHint')}
                    </p>
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center text-[15px] leading-[1.6] text-ink-soft">
                {t('onboarding.waitingForInvite')}
              </p>
            )}
          </CardContent>
        </Card>
      </main>

      {user?.isSuperAdmin && (
        <CreateGroupDialog
          open={createGroupDialogOpen}
          onOpenChange={setCreateGroupDialogOpen}
          onGroupCreated={handleGroupCreated}
        />
      )}
    </div>
  )

  // Render devices tab content
  const renderDevicesContent = () => (
    <>
      {/* Devices section header — serif title with gold rule */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-[28px] font-medium leading-[1.05] tracking-[-0.005em] text-ink">
            {t('devices.title')}
          </h2>
          <hr className="gold-rule mt-3" />
          {selectedGroup && (
            <p className="mt-3 text-[13px] leading-[1.55] text-ink-soft">
              {t('devices.count', { count: filteredDevices.length, group: selectedGroup.name })}
              {!selectedGroup.isAdmin && ' ' + t('common.viewOnly')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {/* Only show Add Device button for group admins with isSuperAdmin */}
          {selectedGroup?.isAdmin && user?.isSuperAdmin && (
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.6} />
              {t('devices.addDevice')}
            </Button>
          )}
        </div>
      </div>

      {/* Device list */}
      {error ? (
        <Card>
          <CardContent className="py-12">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      ) : filteredDevices.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="font-serif text-[22px] font-medium leading-[1.1] text-ink">{t('devices.noDevices')}</p>
            <p className="mt-2 text-[14px] text-ink-soft">{t('devices.noDevicesHint')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full font-sans text-[14px] text-ink">
                <thead className="border-b border-rule bg-paper-2/60">
                  <tr>
                    <th className="px-6 py-3 text-left font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-ink-soft">
                      {t('devices.tableHeaders.device')}
                    </th>
                    <th className="px-6 py-3 text-left font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-ink-soft">
                      {t('devices.tableHeaders.status')}
                    </th>
                    <th className="px-6 py-3 text-left font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-ink-soft">
                      {t('devices.tableHeaders.lastSeen')}
                    </th>
                    <th className="px-6 py-3 text-left font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-ink-soft">
                      {t('devices.tableHeaders.detections')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule/70">
                  {filteredDevices.map((device) => (
                    <tr
                      key={device.id}
                      className="cursor-pointer transition-colors hover:bg-paper-2/40"
                      onClick={() => handleDeviceClick(device.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-serif text-[18px] font-medium leading-[1.15] text-ink">
                            {device.name}
                          </div>
                          <div className="text-[13px] text-ink-soft">
                            {device.location || t('devices.noLocation')}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {device.status === 'online'
                          ? <span className="badge-online">{t('devices.status.online')}</span>
                          : device.status === 'idle'
                          ? <span className="badge-idle">{t('devices.status.idle')}</span>
                          : <span className="badge-offline">{t('devices.status.offline')}</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-[12px] text-ink-soft">
                        {formatDateTime(device.lastSeen)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-[13px] text-ink">
                        {device.totalDetections}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )

  // If loading, show spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-ink"></div>
      </div>
    )
  }

  // If user has no groups, show onboarding
  if (userGroups.length === 0) {
    return renderNoGroupsOnboarding()
  }

  // Normal view with tabs for Devices and Persons
  return (
    <div className="min-h-screen bg-paper">
      {/* Header — Auvers nav: paper-2 strip, museum-label logo lockup, gold rule on bottom edge. */}
      <nav className="border-b border-rule bg-paper-2/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between py-3 sm:h-[68px] gap-3 sm:gap-0 sm:items-center">

            {/* Left: wordmark + group selector */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <a href="/" className="auvers-logo whitespace-nowrap">
                AUVERS
                <span className="product">Sentry Manager</span>
              </a>

              {/* Group Selector + Actions */}
              {userGroups.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  {/* Group select: full width on mobile */}
                  <div className="flex items-center gap-2">
                    <Select value={selectedGroupId || ''} onValueChange={(value) => {
                      if (value === '__create__') {
                        setCreateGroupDialogOpen(true)
                      } else {
                        setSelectedGroupId(value)
                      }
                    }}>
                      <SelectTrigger className="w-full sm:w-[200px]">
                        <SelectValue placeholder={t('groups.selectGroup')} />
                      </SelectTrigger>
                      <SelectContent>
                        {userGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            <span className="flex items-center gap-2">
                              {group.name}
                              {group.isAdmin && <Shield className="h-3 w-3 text-muted-foreground" />}
                            </span>
                          </SelectItem>
                        ))}
                        {user?.isSuperAdmin && (
                          <>
                            <SelectSeparator />
                            <SelectItem value="__create__">
                              <span className="flex items-center gap-2">
                                <Plus className="h-3.5 w-3.5" />
                                {t('groups.createGroup')}
                              </span>
                            </SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>

                    {/* Desktop-only icon buttons */}
                    {!isFeedOnly && (
                      <div className="hidden sm:flex items-center gap-1">
                        {selectedGroupId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenGroupSettings(selectedGroupId)}
                            title={t('groups.groupSettings')}
                            className="shrink-0"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        )}
                        {selectedGroupId && selectedGroup?.isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/members?groupId=${selectedGroupId}`)}
                            className="shrink-0"
                          >
                            <Users className="h-4 w-4 mr-1" />
                            {t('members.title')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile-only full-width action buttons */}
                  {!isFeedOnly && selectedGroupId && (
                    <div className="flex sm:hidden gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenGroupSettings(selectedGroupId)}
                        className="flex-1"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        {t('common.settings')}
                      </Button>
                      {selectedGroup?.isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/members?groupId=${selectedGroupId}`)}
                          className="flex-1"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          {t('members.title')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: User Actions */}
            <div className="flex items-center gap-2 sm:gap-3 justify-end">
              <span className="hidden md:inline-block max-w-[200px] truncate font-mono text-[12px] tracking-wider text-ink-soft">
                {user?.email}
              </span>

              {!isFeedOnly && <TimezoneSelector />}
              <LanguageSwitcher />

              {/* Telegram Link Indicator — tonal, no neon green. Gold dot signals "linked". */}
              {!isFeedOnly && (
                <button
                  onClick={() => setTelegramDialogOpen(true)}
                  className={`flex items-center gap-1.5 rounded-pill border px-2.5 py-1.5 shrink-0 font-sans text-[12px] transition-colors ${
                    telegramLinked
                      ? 'border-gold-soft bg-paper-3 text-ink hover:bg-[#fbf3dd]'
                      : 'border-rule bg-transparent text-ink-soft hover:bg-paper-2'
                  }`}
                  title={telegramLinked ? t('telegram.linked') : t('telegram.linkForAlerts')}
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
                  <span className="hidden sm:inline">{t('telegram.telegram')}</span>
                </button>
              )}

              {/* Logout */}
              <Button variant="ghost" size="sm" onClick={logout} className="shrink-0">
                <LogOut className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content with tabs */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {!isFeedOnly && (
            <TabsList className="mb-6 w-full sm:w-auto">
              <TabsTrigger value="feed" className="flex-1 sm:flex-initial">{t('tabs.feed')}</TabsTrigger>
              {selectedGroup?.isAdmin && (
                <TabsTrigger value="devices" className="flex-1 sm:flex-initial">{t('tabs.devices')}</TabsTrigger>
              )}
              <TabsTrigger value="sightings" className="flex-1 sm:flex-initial">{t('tabs.sightings')}</TabsTrigger>
              <TabsTrigger value="persons" className="flex-1 sm:flex-initial">{t('tabs.persons')}</TabsTrigger>
              {groupPermissions?.canManagePersons && (
                <TabsTrigger value="suggestions" className="flex-1 sm:flex-initial">{t('tabs.suggestions')}</TabsTrigger>
              )}
              <TabsTrigger value="classifications" className="flex-1 sm:flex-initial">{t('tabs.classifications')}</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="feed">
            {selectedGroupId ? (
              <FeedView
                groupId={selectedGroupId}
                isAdmin={!!selectedGroup?.isAdmin}
                permissions={groupPermissions}
                memberAlertContext={{
                  isAdmin: !!selectedGroup?.isAdmin,
                  alertsEnabled: groupAlertPrefs?.alertsEnabled ?? true,
                  alertClassificationIds: groupAlertPrefs?.alertClassificationIds,
                  classificationIds: groupAlertPrefs?.classificationIds ?? [],
                }}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t('sightings.selectGroup')}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="devices">
            {renderDevicesContent()}
          </TabsContent>

          <TabsContent value="sightings">
            {selectedGroupId ? (
              <SightingsTab
                groupId={selectedGroupId}
                onNavigateToPerson={handleNavigateToPerson}
                filterPersonId={searchParams.get('person')}
                filterPersonName={searchParams.get('personName')}
                onClearPersonFilter={handleClearPersonFilter}
                urlSightingId={urlSightingId}
                personOverlayOpen={!!overlayPersonId}
                refreshKey={personsRefreshKey}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t('sightings.selectGroup')}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="persons">
            {selectedGroupId ? (
              <PersonsTab
                groupId={selectedGroupId}
                onNavigateToSightings={handleNavigateToSightings}
                permissions={groupPermissions}
                urlPersonId={urlPersonId}
                isAdmin={selectedGroup?.isAdmin}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t('sightings.selectGroupPersons')}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="suggestions">
            {selectedGroupId ? (
              <MergeSuggestionsTab
                groupId={selectedGroupId}
                canManagePersons={groupPermissions?.canManagePersons ?? false}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t('sightings.selectGroupPersons')}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="classifications">
            {selectedGroupId ? (
              <ClassificationsList
                groupId={selectedGroupId}
                canManageClassifications={groupPermissions?.canManageClassifications ?? false}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t('sightings.selectGroup')}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Person drawer, opened over the current tab rather than routed to.
          The persons tab keeps its own instance (path route /persons/:id); this
          one only ever serves the `?personDetail=` overlay. */}
      {selectedGroupId && overlayPersonId && (
        <PersonDetailDialog
          open
          onOpenChange={(open) => {
            if (!open) handleClosePersonOverlay()
          }}
          personId={overlayPersonId}
          groupId={selectedGroupId}
          onUpdated={() => setPersonsRefreshKey((k) => k + 1)}
          onDeleted={() => {
            setPersonsRefreshKey((k) => k + 1)
            handleClosePersonOverlay()
          }}
          onNavigateToSightings={handleNavigateToSightings}
          canManagePersons={groupPermissions?.canManagePersons ?? true}
          isAdmin={!!selectedGroup?.isAdmin}
        />
      )}

      {/* Dialogs */}
      <AddDeviceDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onDeviceCreated={loadDevices}
      />

      {user?.isSuperAdmin && (
        <CreateGroupDialog
          open={createGroupDialogOpen}
          onOpenChange={setCreateGroupDialogOpen}
          onGroupCreated={handleGroupCreated}
        />
      )}

      {currentGroupId && (
        <GroupSettingsDialog
          open={groupSettingsDialogOpen}
          onOpenChange={setGroupSettingsDialogOpen}
          groupId={currentGroupId}
          onGroupLeft={handleGroupLeft}
          onGroupRenamed={loadGroups}
        />
      )}

      <TelegramLinkDialog
        open={telegramDialogOpen}
        onOpenChange={(open) => {
          setTelegramDialogOpen(open)
          // Refresh the button's linked state after the dialog closes, since
          // the dialog uses its own hook instance to link/unlink.
          if (!open) refetchTelegram()
        }}
      />

    </div>
  )
}
